import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, CheckCircle2, FileSpreadsheet, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import SearchableAgentSelect from "@/components/common/SearchableAgentSelect";
import { cn } from "@/lib/utils";
import {
  buildHireDateImportPreview,
  extractHireSheetRows,
} from "@/utils/agentHireDateExcelImport";

const rowKey = (r) => `${r.agent_id ?? r.excel_name}`;

const AgentHireDateImportModal = ({
  open,
  onClose,
  fileName,
  parsed,
  agents = [],
  isDark,
  textClass,
  labelClass,
  importing,
  parsing = false,
  onConfirm,
  importResult,
  companyLabel,
}) => {
  const [step, setStep] = useState("map");
  const [sheetId, setSheetId] = useState("");
  const [nameCol, setNameCol] = useState(0);
  const [dateCol, setDateCol] = useState(1);
  const [selectedKeys, setSelectedKeys] = useState({});
  const [agentOverrides, setAgentOverrides] = useState({});

  const activeSheet = useMemo(
    () => parsed?.sheets?.find((s) => s.id === sheetId) || parsed?.sheets?.[0] || null,
    [parsed, sheetId],
  );

  useEffect(() => {
    if (!open) return;
    setStep(importResult ? "done" : "map");
    setAgentOverrides({});
    if (!parsed?.sheets?.length) return;
    const first = parsed.sheets[0];
    setSheetId(first.id);
    setNameCol(first.name_col ?? 0);
    setDateCol(first.date_col ?? 1);
  }, [open, parsed, importResult]);

  useEffect(() => {
    if (!activeSheet) return;
    setNameCol(activeSheet.name_col ?? 0);
    setDateCol(activeSheet.date_col ?? 1);
  }, [activeSheet]);

  const { excelRows, excelMissingDateRows } = useMemo(() => {
    if (!activeSheet) return { excelRows: [], excelMissingDateRows: [] };
    const { withDate, missingDate } = extractHireSheetRows(activeSheet, nameCol, dateCol);
    return { excelRows: withDate, excelMissingDateRows: missingDate };
  }, [activeSheet, nameCol, dateCol]);

  const preview = useMemo(() => {
    if (!excelRows.length) return null;
    const overrideIds = {};
    Object.entries(agentOverrides).forEach(([name, agent]) => {
      if (agent?.id) overrideIds[name] = agent.id;
    });
    return buildHireDateImportPreview(excelRows, agents, overrideIds);
  }, [excelRows, agents, agentOverrides]);

  useEffect(() => {
    if (!preview || step === "done") return;
    const sel = {};
    preview.to_update.forEach((r) => { sel[rowKey(r)] = true; });
    setSelectedKeys(sel);
    if (preview.to_update.length > 0 || preview.unmatched.length === 0) {
      setStep("preview");
    }
  }, [preview, step, sheetId, nameCol, dateCol]);

  const selectedRows = useMemo(() => {
    if (!preview) return [];
    return preview.to_update.filter((r) => selectedKeys[rowKey(r)]);
  }, [preview, selectedKeys]);

  const handleConfirm = useCallback(() => {
    if (!selectedRows.length) return;
    onConfirm({
      source_file: fileName,
      rows: selectedRows.map((r) => ({
        agent_id: Number(r.agent_id),
        ise_giris_tarihi: r.new_date,
        excel_name: r.excel_name,
      })),
    });
  }, [selectedRows, onConfirm, fileName]);

  const panelClass = isDark ? "bg-slate-900 border-slate-700 text-white" : "";
  const blockClass = isDark ? "border-slate-700 bg-slate-950/50" : "border-gray-200 bg-gray-50/80";
  const filterTriggerClass = cn(
    "h-9 min-h-9 w-full rounded-md border text-sm font-normal shadow-sm",
    isDark ? "border-slate-700 bg-slate-900 text-slate-100" : "border-input bg-background",
  );
  const selectClass = cn("h-9 rounded-md border px-2 text-sm w-full", isDark ? "bg-slate-900 border-slate-700" : "border-gray-300");

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !importing && !parsing && onClose()}>
      <DialogContent className={`max-w-3xl max-h-[92vh] overflow-hidden flex flex-col ${panelClass}`}>
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2 pr-6">
            <FileSpreadsheet className={`w-5 h-5 shrink-0 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
            İşe giriş tarihi Excel
          </DialogTitle>
          <div className="space-y-0.5">
            {companyLabel && (
              <p className={`text-xs font-medium ${isDark ? "text-blue-300" : "text-blue-700"}`}>
                Firma: {companyLabel}
              </p>
            )}
            {fileName && <p className={`text-xs font-normal ${labelClass}`}>{fileName}</p>}
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-1 pr-1">
          {parsing && (
            <div className={`rounded-lg border p-6 flex flex-col items-center gap-3 ${blockClass}`}>
              <Loader2 className={`w-8 h-8 animate-spin ${isDark ? "text-blue-400" : "text-blue-600"}`} />
              <p className={`text-sm ${textClass}`}>Excel okunuyor…</p>
            </div>
          )}

          {!parsing && parsed?.error && (
            <p className={`text-sm ${isDark ? "text-red-300" : "text-red-700"}`}>{parsed.error}</p>
          )}

          {!parsing && step === "done" && importResult && (
            <div className={`rounded-lg border p-4 ${blockClass}`}>
              <div className="flex items-start gap-3">
                <CheckCircle2 className={`w-8 h-8 shrink-0 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-semibold ${textClass}`}>
                    {importResult.updated_count ?? 0} agent güncellendi
                  </p>
                  {(importResult.updated || []).length > 0 && (
                    <ul className={`text-xs mt-3 space-y-1 max-h-48 overflow-y-auto ${labelClass}`}>
                      {importResult.updated.map((u) => (
                        <li key={u.agent_id}>
                          <span className={textClass}>{u.agent_name}</span>
                          {" · "}
                          <span className="tabular-nums">{u.old_date || "—"}</span>
                          {" → "}
                          <span className={`tabular-nums font-medium ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>{u.new_date}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {(importResult.skipped ?? 0) > 0 && (
                    <p className={`text-xs mt-2 ${labelClass}`}>{importResult.skipped} satır atlandı (aynı tarih veya geçersiz)</p>
                  )}
                  {(importResult.skipped_details?.length ?? 0) > 0 && (
                    <ul className={`text-[11px] mt-2 space-y-1 max-h-32 overflow-y-auto ${labelClass}`}>
                      {importResult.skipped_details.slice(0, 20).map((s, i) => (
                        <li key={`${s.agent_id}-${i}`}>
                          {s.agent_name || s.excel_name || `Agent #${s.agent_id}`}
                          {" — "}
                          {s.reason}
                          {s.date ? ` (${s.date})` : ""}
                        </li>
                      ))}
                    </ul>
                  )}
                  {(importResult.errors?.length ?? 0) > 0 && (
                    <ul className={`text-[11px] mt-2 text-red-400 space-y-1`}>
                      {importResult.errors.slice(0, 10).map((e, i) => (
                        <li key={i}>#{e.agent_id}: {e.reason}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {!parsing && step !== "done" && parsed?.sheets?.length > 0 && (
            <>
              <div className={`rounded-lg border p-3 space-y-3 ${blockClass}`}>
                <p className={`text-xs font-medium ${textClass}`}>Sayfa ve sütun eşleştirme</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className={`text-[11px] ${labelClass}`}>Sheet</label>
                    <select
                      className={`mt-1 ${selectClass}`}
                      value={sheetId}
                      onChange={(e) => setSheetId(e.target.value)}
                    >
                      {parsed.sheets.map((s) => (
                        <option key={s.id} value={s.id}>{s.sheet_name} ({s.row_count} satır)</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`text-[11px] ${labelClass}`}>Ad soyad sütunu</label>
                    <select className={`mt-1 ${selectClass}`} value={nameCol} onChange={(e) => setNameCol(Number(e.target.value))}>
                      {(activeSheet?.headers || []).map((h) => (
                        <option key={h.col} value={h.col}>{h.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`text-[11px] ${labelClass}`}>İşe giriş sütunu</label>
                    <select className={`mt-1 ${selectClass}`} value={dateCol} onChange={(e) => setDateCol(Number(e.target.value))}>
                      {(activeSheet?.headers || []).map((h) => (
                        <option key={h.col} value={h.col}>{h.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {activeSheet?.preview_rows?.length > 0 && (
                  <div className="overflow-x-auto text-[10px] border rounded-md">
                    <table className="w-full">
                      <thead>
                        <tr className={isDark ? "bg-slate-800" : "bg-gray-100"}>
                          {(activeSheet.headers || []).slice(0, 8).map((h) => (
                            <th key={h.col} className="px-2 py-1 text-left font-medium">{h.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {activeSheet.preview_rows.slice(0, 5).map((line, i) => (
                          <tr key={i} className={isDark ? "border-t border-slate-800" : "border-t border-gray-200"}>
                            {(activeSheet.headers || []).slice(0, 8).map((h) => (
                              <td key={h.col} className="px-2 py-1 whitespace-nowrap max-w-[120px] truncate">
                                {line[h.col] != null ? String(line[h.col]) : ""}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className={`text-[11px] ${labelClass}`}>
                  Excel&apos;den <strong>{excelRows.length}</strong> satır (ad + tarih)
                  {excelMissingDateRows.length > 0 && (
                    <> · <span className={isDark ? "text-amber-300" : "text-amber-800"}>{excelMissingDateRows.length} satırda Excel tarihi boş</span></>
                  )}
                </p>
              </div>

              {preview && (
                <>
                  <div className={`rounded-lg border p-3 ${blockClass}`}>
                    <p className={`text-xs ${textClass}`}>
                      <strong>{preview.update_count}</strong> güncelleme
                      {preview.unchanged_count > 0 && <> · <strong>{preview.unchanged_count}</strong> zaten aynı</>}
                      {preview.unmatched_count > 0 && (
                        <> · <strong className={isDark ? "text-amber-300" : "text-amber-800"}>{preview.unmatched_count}</strong> eşleşmedi</>
                      )}
                    </p>
                    {(preview.panel_missing_not_reached?.length ?? 0) > 0 && (
                      <p className={`text-[11px] mt-2 ${isDark ? "text-sky-200" : "text-sky-800"}`}>
                        Panelde işe girişi boş <strong>{preview.panel_missing_hire_count}</strong> agent var;
                        {" "}<strong>{preview.panel_missing_not_reached.length}</strong> tanesi bu importta hiç eşleşmedi (Excel&apos;de yok / isim farklı / Excel tarihi boş).
                      </p>
                    )}
                  </div>

                  {excelMissingDateRows.length > 0 && (
                    <details className={`rounded-lg border p-3 text-xs ${isDark ? "border-amber-700/40 bg-amber-950/20" : "border-amber-200 bg-amber-50/90"}`}>
                      <summary className={`cursor-pointer font-medium ${isDark ? "text-amber-100" : "text-amber-900"}`}>
                        Excel&apos;de işe giriş hücresi boş ({excelMissingDateRows.length})
                      </summary>
                      <p className={`mt-2 text-[11px] ${labelClass}`}>
                        Bu isimler için Excel&apos;de «İşe Giriş Tarihi» doldurulmadan panel güncellenemez.
                      </p>
                      <ul className={`mt-2 max-h-32 overflow-y-auto space-y-0.5 ${labelClass}`}>
                        {excelMissingDateRows.slice(0, 40).map((r) => (
                          <li key={r.row_number}>{r.excel_name} <span className="opacity-70">(satır {r.row_number})</span></li>
                        ))}
                      </ul>
                    </details>
                  )}

                  {(preview.panel_missing_not_reached?.length ?? 0) > 0 && (
                    <details className={`rounded-lg border p-3 text-xs ${blockClass}`}>
                      <summary className={`cursor-pointer font-medium ${textClass}`}>
                        Panelde boş işe giriş — import kapsamında değil ({preview.panel_missing_not_reached.length})
                      </summary>
                      <ul className={`mt-2 max-h-36 overflow-y-auto space-y-0.5 ${labelClass}`}>
                        {preview.panel_missing_not_reached.slice(0, 50).map((a) => (
                          <li key={a.agent_id}>
                            {a.agent_name}
                            {a.kullanici_adi ? ` · @${a.kullanici_adi}` : ""}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}

                  {preview.unmatched.length > 0 && (
                    <div className={`rounded-lg border p-3 space-y-3 ${isDark ? "border-amber-700/40 bg-amber-950/20" : "border-amber-200 bg-amber-50/90"}`}>
                      <div className="flex gap-2">
                        <AlertTriangle className={`w-5 h-5 shrink-0 ${isDark ? "text-amber-400" : "text-amber-700"}`} />
                        <p className={`text-xs ${isDark ? "text-amber-100" : "text-amber-900"}`}>
                          Eşleşmeyen Excel isimleri — isterseniz panel agent seçin (kayda dahil etmezseniz atlanır).
                        </p>
                      </div>
                      <ul className="space-y-2 max-h-36 overflow-y-auto">
                        {preview.unmatched.map((u) => (
                          <li key={u.excel_name} className={`text-xs rounded border p-2 ${isDark ? "border-slate-700" : "border-amber-100"}`}>
                            <span className={textClass}>{u.excel_name}</span>
                            <span className={`ml-2 tabular-nums ${labelClass}`}>{u.ise_giris_tarihi}</span>
                            <div className="mt-2">
                              <SearchableAgentSelect
                                agents={agents}
                                value={agentOverrides[u.excel_name] ?? null}
                                onChange={(agent) => setAgentOverrides((prev) => ({ ...prev, [u.excel_name]: agent }))}
                                placeholder="Agent eşleştir…"
                                isDark={isDark}
                                inputClass={filterTriggerClass}
                                labelClass={labelClass}
                                size="default"
                                inDialog
                              />
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {preview.to_update.length > 0 && (
                    <div className={`rounded-lg border p-3 ${blockClass}`}>
                      <p className={`text-xs font-medium mb-2 ${textClass}`}>Güncellenecek kayıtlar (işaretli olanlar kaydedilir)</p>
                      <ul className="space-y-1 max-h-[min(40vh,320px)] overflow-y-auto text-xs">
                        {preview.to_update.map((r) => (
                          <li
                            key={rowKey(r)}
                            className={`flex flex-wrap items-center gap-2 rounded px-2 py-1.5 ${isDark ? "hover:bg-slate-800/60" : "hover:bg-gray-100"}`}
                          >
                            <Checkbox
                              checked={!!selectedKeys[rowKey(r)]}
                              onCheckedChange={(v) => setSelectedKeys((prev) => ({ ...prev, [rowKey(r)]: v === true }))}
                            />
                            <span className={`font-medium min-w-[120px] ${textClass}`}>{r.agent_name}</span>
                            {r.excel_name !== r.agent_name && (
                              <span className={`${labelClass}`}>(Excel: {r.excel_name})</span>
                            )}
                            <span className={`tabular-nums ml-auto ${labelClass}`}>
                              {r.panel_hire_was_empty ? (
                                <>
                                  <span className={isDark ? "text-amber-300" : "text-amber-700"}>boş</span>
                                  {" → "}
                                  <span className={isDark ? "text-emerald-300" : "text-emerald-700"}>{r.new_date}</span>
                                </>
                              ) : (
                                <>
                                  {r.old_date || "—"}
                                  {" → "}
                                  <span className={isDark ? "text-emerald-300" : "text-emerald-700"}>{r.new_date}</span>
                                </>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {preview.unchanged.length > 0 && (
                    <details className={`rounded-lg border p-3 text-xs ${blockClass}`}>
                      <summary className={`cursor-pointer font-medium ${textClass}`}>
                        Zaten aynı ({preview.unchanged_count}) — eşleşti ama tarih değişmiyor
                      </summary>
                      <ul className={`mt-2 space-y-1 max-h-40 overflow-y-auto ${labelClass}`}>
                        {preview.unchanged.slice(0, 30).map((r) => (
                          <li key={`u-${r.agent_id}-${r.excel_name}`}>
                            {r.agent_name}
                            {" · "}
                            <span className="tabular-nums">{r.new_date || r.old_date || "—"}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}

                  {preview.to_update.length === 0 && preview.unmatched.length === 0 && excelRows.length > 0 && (
                    <p className={`text-sm ${labelClass}`}>Tüm tarihler zaten panel ile aynı; güncelleme yok.</p>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2 shrink-0">
          {step === "done" ? (
            <Button type="button" onClick={onClose}>Tamam</Button>
          ) : (
            <>
              <Button type="button" variant="outline" disabled={importing || parsing} onClick={onClose}>İptal</Button>
              <Button
                type="button"
                disabled={importing || parsing || selectedRows.length === 0}
                onClick={handleConfirm}
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {selectedRows.length > 0 ? `${selectedRows.length} tarihi kaydet` : "Güncellenecek kayıt yok"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgentHireDateImportModal;
